import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import Icon from '../Icon';

interface Guarantee {
  icon: string;
  label: string;
  value: string;
  note?: string;
}

const guarantees: Guarantee[] = [
  {
    icon: 'speed',
    label: 'P50 Latency',
    value: '~15-25ms',
    note: 'Bid placement to broadcast',
  },
  {
    icon: 'show_chart',
    label: 'P99 Latency',
    value: '~50-80ms',
    note: 'Under normal load',
  },
  {
    icon: 'rocket_launch',
    label: 'Cold Start',
    value: '~50-100ms',
    note: 'First request to idle auction',
  },
  {
    icon: 'bedtime',
    label: 'Hibernation Wake',
    value: '~10-20ms',
    note: 'WebSocket reconnect',
  },
  {
    icon: 'replay',
    label: 'Crash Recovery',
    value: '< 1 second',
    note: 'Event replay on restart',
  },
  {
    icon: 'verified',
    label: 'Consistency',
    value: 'Linearizable',
    note: 'Per-auction ordering',
  },
];

export default function OperationalGuarantees() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <section ref={ref} className="py-10 px-4 bg-gradient-to-r from-gray-100 to-gray-50 border-t border-gray-200">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center gap-2 mb-6"
        >
          <Icon name="monitoring" className="text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            Operational Expectations
          </h3>
        </motion.div>

        {/* Guarantees Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {guarantees.map((g, i) => (
            <motion.div
              key={g.label}
              initial={{ opacity: 0, y: 10 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.3, delay: 0.05 * i }}
              className="bg-white rounded-xl p-4 text-center border border-gray-200 shadow-sm"
            >
              <Icon name={g.icon} className="text-2xl text-purple-500 mb-2" />
              <div className="text-lg font-bold text-gray-900">{g.value}</div>
              <div className="text-xs font-medium text-gray-700">{g.label}</div>
              {g.note && (
                <div className="text-xs text-gray-400 mt-1">{g.note}</div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Disclaimer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center text-xs text-gray-400 mt-6"
        >
          <Icon name="info" size="small" className="inline mr-1" />
          Latency expectations are approximate and may vary based on geographic location and network conditions.
          Measured from Cloudflare edge to Durable Object.
        </motion.p>
      </div>
    </section>
  );
}
